import { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Avatar,
  Paper,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
  Grid,
  TextareaAutosize,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from "@mui/material";
import {
  Person,
  Security,
  Lock,
  CreditCard,
  Edit,
  Discord,
  Twitter,
  Instagram,
  Telegram,
} from "@mui/icons-material";
import zIndex from "@mui/material/styles/zIndex";
import { darkColors } from "../../theme/colors";
import { assetIcons } from "../../constants/assets";
import XIcon from "@mui/icons-material/X";
import englishTranslations from "../../i18n/translations/en";
import { socialRedirectUrls } from "../../constants/urls";
import { fontSizes } from "../../theme/font-sizes";
import AccountSidebar from "./account-sidebar";
import { StyledTextField, StyledTypography } from "./account";
import GradientButton from "../utils/gradient-button";
import { useNavigate } from "react-router-dom";
import routes from "../../constants/routes";
import useApiRequest from "../../api/api";
import apiRoutes from "../../api/routes";
import LoadingBackdrop from "../utils/loader";
import { useUser } from "../../context/user-context";
import InlineSvg from "../utils/inline-svg";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
export default function SecurityComponent() {
  const navigate = useNavigate();

  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const { makeApiCall } = useApiRequest();
  const [isLoading, setIsLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false); // State for old password visibility
  const [showNewPassword, setShowNewPassword] = useState(false); // State for new password visibility
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // State for confirm password visibility

  const [profileData, setProfileData] = useState({
    password: "",
    oldPassword: "",
    confirmPassword: "",
  });
  // const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const { user, updateUser } = useUser();
  const [buttonText, setButtonText] = useState(
    user.is2FA ? "REMOVE 2FA SECURITY" : "ADD 2FA SECURITY"
  );
  const navigateToSettings = () => {
    navigate(routes.settings);
  };
  useEffect(() => {
    setButtonText(user.is2FA ? "REMOVE 2FA SECURITY" : "ADD 2FA SECURITY");
  }, [user]);
  const toggle2FA = async () => {
    setIsLoading(true);
    const response = await makeApiCall(
      apiRoutes.profile.authorize,
      "PUT",
      {},
      "show"
    );

    if (response.success) {
      // Toggle the 2FA status
      const updatedUser = {
        ...user,
        is2FA: !user.is2FA,
      };

      // Update the localStorage with the new user data
      localStorage.setItem("user", JSON.stringify(updatedUser));
      updateUser(updatedUser);

      // Update the button text based on the new 2FA status
      setButtonText(
        updatedUser.is2FA ? "REMOVE 2FA SECURITY" : "ADD 2FA SECURITY"
      );
      setIsLoading(false);
    }
  };
  const changePassword = async () => {
    setIsLoading(true);
    try {
      const payload = {
        currentPassword: user.isPasswordSet ? profileData.oldPassword : null,
        newPassword: profileData.password,
        retypedPassword: profileData.confirmPassword,
      };
      const response = await makeApiCall(
        apiRoutes.profile.password,
        "PUT",
        payload,
        "show"
      );
      if (response && response.success) {
        setProfileData({
          password: "",
          oldPassword: "",
          confirmPassword: "",
        });
        const updatedUser = {
          ...user,
          isPasswordSet: true,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        updateUser(updatedUser);
      }
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  };

  const toggleOldPasswordVisibility = () => {
    setShowOldPassword((prev) => !prev);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };
  return (
    <Box sx={{ display: "flex", bgcolor: "#171717", height: "100vh" }}>
      {/* Sidebar */}
      {!isMediumScreen && <AccountSidebar />}
      <LoadingBackdrop isLoading={isLoading} />

      {/* Main content */}
      <Box
        // component="main"
        display={"flex-col"}
        sx={{
          //   flexGrow: 1,
          p: isMediumScreen ? 4 : 10,
          width: "100%",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            display: "none", // Hide scrollbar in WebKit browsers (Chrome, Safari)
          },
          pt: isMediumScreen ? 5 : 8,
          mb: 7,
          bgcolor: "#121212",
          color: "#fff",
        }}
      >
        {/* <Container maxWidth=""> */}
        <Stack
          onClick={navigateToSettings}
          direction={"row"}
          sx={{ cursor: "pointer" }}
          alignItems="center"
        >
          {isMediumScreen && (
            <InlineSvg
              sx={{
                cursor: "pointer",
                display: "inline-block",
                width: 20,
                mr: 3,
                pb: 4,
              }}
              url={assetIcons.leftArrow}
            />
          )}
          <Typography
            fontFamily={"MontserratBold"}
            component="span"
            fontSize={
              isMediumScreen ? fontSizes.heading2 : fontSizes.superHeading5
            }
            textAlign={"left"}
            sx={{ mb: isMediumScreen ? 4 : 6 }}
          >
            Security
          </Typography>{" "}
        </Stack>

        <Grid container spacing={6}>
          {/* Profile Picture Section */}
          <Grid item xs={12}>
            <Stack
              direction={isMediumScreen ? "column" : "row"}
              spacing={isMediumScreen ? 3 : "11.5%"}
              justifyContent={isMediumScreen ? "start" : "start"}
              alignItems="start"
            >
              <Stack>
                <Typography
                  fontFamily={"MontserratBold"}
                  fontSize={fontSizes.heading3}
                  textAlign={"left"}
                  sx={{ width: 160 }}
                >
                  Change Password
                </Typography>
              </Stack>
              <Stack spacing={3} width={isMediumScreen ? 300 : ""}>
                {user.isPasswordSet && (
                  <>
                    <StyledTextField
                      isMediumScreen={isMediumScreen}
                      value={profileData.oldPassword}
                      variant="outlined"
                      type={showOldPassword ? "text" : "password"}
                      size="small"
                      placeholder="Old Password"
                      onChange={(e) =>
                        setProfileData((prev) => ({
                          ...prev,
                          oldPassword: e.target.value,
                        }))
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={toggleOldPasswordVisibility}
                              className="white-icon"
                            >
                              {showOldPassword ? (
                                <VisibilityOutlinedIcon />
                              ) : (
                                <VisibilityOffOutlinedIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </>
                )}

                <StyledTextField
                  isMediumScreen={isMediumScreen}
                  value={profileData.password}
                  variant="outlined"
                  type={showNewPassword ? "text" : "password"} // Toggle between text and password
                  size="small"
                  placeholder="New Password"
                  onChange={(e) =>
                    setProfileData({
                      ...profileData,
                      password: e.target.value,
                    })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={toggleNewPasswordVisibility}
                          className="white-icon"
                        >
                          {showNewPassword ? (
                            <VisibilityOutlinedIcon />
                          ) : (
                            <VisibilityOffOutlinedIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledTextField
                  isMediumScreen={isMediumScreen}
                  value={profileData.confirmPassword}
                  variant="outlined"
                  type={showConfirmPassword ? "text" : "password"} // Toggle between text and password
                  size="small"
                  placeholder="Confirm New Password"
                  onChange={(e) =>
                    setProfileData({
                      ...profileData,
                      confirmPassword: e.target.value,
                    })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={toggleConfirmPasswordVisibility}
                          className="white-icon"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOutlinedIcon />
                          ) : (
                            <VisibilityOffOutlinedIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <GradientButton
                  width={isMediumScreen ? 300 : 460}
                  text={"CHANGE PASSWORD"}
                  onClick={changePassword}
                  disabled={
                    !(
                      (user.isPasswordSet ? profileData.oldPassword : true) &&
                      profileData.password &&
                      profileData.confirmPassword
                    )
                  }
                />
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        <Box mt={8}>
          <Divider
            sx={{ background: darkColors.gray, mr: isMediumScreen ? 0 : 8 }}
          />
        </Box>

        <Grid container spacing={5} mb={isMediumScreen ? 8 : 0} mt={2}>
          {/* Username Section */}
          <Grid item xs={12}>
            <Stack
              direction={isMediumScreen ? "column" : "row"}
              spacing={isMediumScreen ? 4 : 37}
              //   justifyContent={"space-between"}
              alignItems={isMediumScreen ? "start" : "center"}
            >
              <Stack>
                <Typography
                  fontFamily={"MontserratBold"}
                  fontSize={fontSizes.heading3}
                  textAlign={"left"}
                  sx={{ width: 230 }}
                >
                  Two Factor Authentication
                </Typography>
              </Stack>
              <GradientButton text={buttonText} onClick={toggle2FA} />
            </Stack>
          </Grid>
        </Grid>
        {/* </Container> */}
      </Box>
    </Box>
  );
}
